import compact from 'lodash/compact';
import noop from 'lodash/noop';
import { ReactElement, useMemo } from 'react';

import { Patient } from '@dreryk/egabinet-patient-frontend-api';
import {
   PatientContinuouslyUsedMedicineExtended,
   PatientContinuouslyUsedMedicineExtendedQuery,
} from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { Ordering } from '@dreryk/react-common';
import {
   AddCircleIcon,
   createDynamicColumnsWidth,
   IconWrapper,
   Padding,
   ReplaceMedicineIcon,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import {
   ContinuouslyUsedMedicineButton,
   MedicineFullNameCell,
   MedicinePackage,
   MedicineSafetyIndicators,
} from '~libs.desktop.shared.medicine';

import { medicineSelectorTranslationKeys } from '../../../../../translations';
import { OnContinuouslyUsedMedicineSelection, OnMedicineSelection } from '../../../PrescriptionIssuingPanel.types';
import { AddMedicineToPrescriptionTooltip } from '../AddMedicineToPrescriptionTooltip';
import { FavoriteMedicineIcon } from '../FavoriteMedicinesSelectorTable/FavoriteMedicineIcon';
import { COMMERCIAL_MEDICINE_NAME_COLUMN } from '../MedicineSelectorTable.hooks';
import { MedicineReplacementModalOpenAction } from '../../../medicineReplacementModal/MedicineReplacementModalOpenAction';

const DEFAULT_ORDERINGS: Ordering<any>[] = [];

export const useContinuouslyUsedMedicineSelectorConfiguration = (
   patientId: Patient['id']
): LazyLoadedDataConfigurationByCqrsQuery<PatientContinuouslyUsedMedicineExtended> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<PatientContinuouslyUsedMedicineExtended>(() => {
      const continuouslyUsedMedicineQuery = new PatientContinuouslyUsedMedicineExtendedQuery();
      continuouslyUsedMedicineQuery.patientId = patientId;
      return continuouslyUsedMedicineQuery;
   }, DEFAULT_ORDERINGS);
};

type ContinuouslyUsedMedicineSelectorTableColumnsOptions = {
   onMedicineSelection: OnMedicineSelection;
   onContinuouslyUsedMedicineSelection: OnContinuouslyUsedMedicineSelection;
   patientId: Patient['id'];
   onMedicineProcessed: VoidFunction;
   visitDiagnoses: VisitDiagnosis[];
};

// eslint-disable-next-line max-lines-per-function
export const useContinuouslyUsedMedicineSelectorTableColumns = (
   options: ContinuouslyUsedMedicineSelectorTableColumnsOptions
): TableColumnConfiguration<PatientContinuouslyUsedMedicineExtended>[] => {
   const medicineDatabaseExtendedAvailable = protectFeature('extendedMedicineDatabase');
   // eslint-disable-next-line max-lines-per-function
   return useMemo<TableColumnConfiguration<PatientContinuouslyUsedMedicineExtended>[]>(() => {
      const columnsWidths = createDynamicColumnsWidth({
         [COMMERCIAL_MEDICINE_NAME_COLUMN]: medicineDatabaseExtendedAvailable ? '65%' : '50%',
         safetyIndicators: medicineDatabaseExtendedAvailable ? '82px' : '0px',
         package: medicineDatabaseExtendedAvailable ? '35%' : '50%',
         options: '72px',
      });
      return compact([
         {
            name: COMMERCIAL_MEDICINE_NAME_COLUMN,
            accessor: noop,
            label: medicineSelectorTranslationKeys.table.commercialName,
            width: columnsWidths[COMMERCIAL_MEDICINE_NAME_COLUMN],

            renderer: (
               props: ValueRendererProps<
                  PatientContinuouslyUsedMedicineExtended,
                  PatientContinuouslyUsedMedicineExtended
               >
            ): ReactElement => {
               return (
                  <MedicineFullNameCell
                     onMedicineSelection={options.onMedicineSelection}
                     patientMedicine={props.bean.patientMedicine}
                     visitDiagnoses={options.visitDiagnoses}
                  />
               );
            },
            sortingDisabled: true,
         },
         medicineDatabaseExtendedAvailable
            ? {
                 name: 'safetyIndicators',
                 accessor: noop,
                 width: columnsWidths.safetyIndicators,

                 renderer: (props: ValueRendererProps<PatientContinuouslyUsedMedicineExtended>) => (
                    <MedicineSafetyIndicators medicineId={props.bean.patientMedicine.medicine.id} />
                 ),
                 sortingDisabled: true,
              }
            : undefined,
         {
            name: 'package',
            accessor: noop,
            label: medicineSelectorTranslationKeys.table.package,
            width: columnsWidths.package,

            renderer: (
               props: ValueRendererProps<
                  PatientContinuouslyUsedMedicineExtended,
                  PatientContinuouslyUsedMedicineExtended
               >
            ): ReactElement => (
               <MedicinePackage
                  form={props.bean.patientMedicine.medicine.form}
                  dose={props.bean.patientMedicine.medicine.dose}
                  packageContent={props.bean.patientMedicine.medicine.packageContent}
                  valid={props.bean.patientMedicine.medicine.valid}
               />
            ),
            sortingDisabled: true,
         },
         {
            name: 'options',
            accessor: noop,
            label: '',

            renderer: (
               props: ValueRendererProps<
                  PatientContinuouslyUsedMedicineExtended,
                  PatientContinuouslyUsedMedicineExtended
               >
            ): ReactElement => {
               const valid = props.bean.patientMedicine.medicine.valid !== false;
               return (
                  <>
                     <ContinuouslyUsedMedicineButton
                        continuouslyUsedMedicineId={props.bean.patientContinuouslyUsedMedicine.id}
                        patientId={options.patientId}
                        medicineName={props.bean.patientMedicine.medicine.fullName}
                        medicineId={props.bean.patientMedicine.medicine.id}
                        medicineValid={Boolean(props.bean.patientMedicine.medicine.valid)}
                        onMedicineProcessed={options.onMedicineProcessed}
                     />
                     {valid ? (
                        <FavoriteMedicineIcon
                           medicineId={props.bean.patientMedicine.medicine.id}
                           medicineValid={Boolean(props.bean.patientMedicine.medicine.valid)}
                           favorite={Boolean(props.bean.patientMedicine.favorite)}
                           onMedicineProcessed={options.onMedicineProcessed}
                        />
                     ) : null}
                     {valid ? (
                        <AddMedicineToPrescriptionTooltip>
                           <IconWrapper
                              testId={'addMedicineToPrescription'}
                              onClick={() =>
                                 options.onContinuouslyUsedMedicineSelection(
                                    props.bean.patientMedicine,
                                    props.bean.patientContinuouslyUsedMedicine
                                 )
                              }
                           >
                              <AddCircleIcon />
                           </IconWrapper>
                        </AddMedicineToPrescriptionTooltip>
                     ) : null}
                     {valid ? null : (
                        <MedicineReplacementModalOpenAction
                           withdrawnMedicine={props.bean.patientMedicine.medicine}
                           patientId={options.patientId}
                           medicineId={props.bean.patientContinuouslyUsedMedicine.id}
                           visitDiagnoses={options.visitDiagnoses}
                           source={'continuouslyUsedMedicine'}
                        >
                           {({ openModal }) => (
                              <IconWrapper testId={'replaceMedicine'} onClick={openModal}>
                                 <ReplaceMedicineIcon />
                              </IconWrapper>
                           )}
                        </MedicineReplacementModalOpenAction>
                     )}
                  </>
               );
            },
            width: columnsWidths.options,
            padding: Padding.NONE,
            sortingDisabled: true,
         },
      ]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [
      medicineDatabaseExtendedAvailable,
      options.patientId,
      options.onMedicineSelection,
      options.onContinuouslyUsedMedicineSelection,
   ]);
};
